import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useMutation, useQuery } from "urql";
import {
  Button,
  Card,
  ErrorState,
  LoadingState,
  PageHeader,
  Tag,
  NotificationsDocument,
  MarkNotificationsReadDocument,
  formatJstDateTime,
  type NotificationKind,
} from "visumo-data-agent-web-shared";
import { HelpIcon } from "../help/HelpIcon";
const LABELS: Record<NotificationKind, string> = {
  JOB_SUCCEEDED: "完了",
  JOB_SUCCEEDED_WITH_ERRORS: "一部失敗",
  JOB_FAILED: "失敗",
  BUDGET_EXHAUSTED: "予算の上限",
  DELEGATION_STARTED: "代行の開始",
  DELEGATION_ENDED: "代行の終了",
};
export function NotificationsPage() {
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [after, setAfter] = useState<string | null>(null);
  const context = useMemo(
    () => ({ additionalTypenames: ["Notification", "NotificationConnection"] }),
    [],
  );
  const [result, refetch] = useQuery({
    query: NotificationsDocument,
    variables: { first: 20, after, unreadOnly },
    context,
  });
  const [marked, mark] = useMutation(MarkNotificationsReadDocument);
  const navigate = useNavigate();
  const connection = result.data?.notifications;
  async function read(id: string, route: string | null | undefined) {
    const response = await mark(
      { input: { notificationIds: [id] } },
      { additionalTypenames: ["Notification", "NotificationConnection"] },
    );
    if (
      response.error ||
      response.data?.markNotificationsRead.userErrors.length
    )
      return;
    if (route) void navigate(route);
    else refetch({ requestPolicy: "network-only" });
  }
  return (
    <div className="vd-stack">
      <PageHeader
        title="通知"
        description="処理の結果と、サポートによる代行の開始・終了を確認できます。通知は180日間残ります。"
        titleExtra={<HelpIcon slug="notifications/inbox" />}
      />
      <div className="vd-inline">
        <label>
          <input
            type="checkbox"
            checked={unreadOnly}
            onChange={(event) => {
              setUnreadOnly(event.target.checked);
              setAfter(null);
            }}
          />{" "}
          未読だけ
        </label>
        <Button
          disabled={
            marked.fetching || !connection || connection.unreadCount === 0
          }
          onClick={() => {
            void mark(
              { input: { all: true } },
              {
                additionalTypenames: ["Notification", "NotificationConnection"],
              },
            ).then(() => {
              setAfter(null);
              refetch({ requestPolicy: "network-only" });
            });
          }}
        >
          すべて既読にする
        </Button>
      </div>
      <p className="vd-stat-note">
        未読数と「すべて既読にする」は直近200件が対象です。
      </p>
      {marked.error ? <div role="alert">{marked.error.message}</div> : null}
      {marked.data?.markNotificationsRead.userErrors.map((error) => (
        <div key={error.code} role="alert">
          {error.message}
        </div>
      ))}
      {result.error ? (
        <ErrorState
          title="通知を読み込めませんでした"
          description={result.error.message}
          onRetry={() => refetch({ requestPolicy: "network-only" })}
        />
      ) : !connection ? (
        <LoadingState label="通知を読み込んでいます" />
      ) : (
        <>
          {connection.edges.length === 0 ? (
            <Card
              title={
                unreadOnly
                  ? "未読の通知はありません"
                  : "通知はまだありません。時間のかかる処理が終わると、ここに届きます。"
              }
            >
              {null}
            </Card>
          ) : (
            connection.edges.map(({ node }) => (
              <Card
                key={node.id}
                title={node.title}
                extra={
                  <>
                    <Tag>{LABELS[node.kind]}</Tag>
                    {node.readAt === null ? <Tag>未読</Tag> : null}
                  </>
                }
              >
                <p>{node.body}</p>
                <p className="vd-stat-note">
                  {formatJstDateTime(node.createdAt)}
                </p>
                <Button
                  disabled={marked.fetching}
                  onClick={() => {
                    void read(node.id, node.route);
                  }}
                >
                  {node.route ? "確認する" : "既読にする"}
                </Button>
              </Card>
            ))
          )}
          {connection.pageInfo.hasNextPage ? (
            <Button
              disabled={result.fetching}
              onClick={() => setAfter(connection.pageInfo.endCursor ?? null)}
            >
              続きを読む
            </Button>
          ) : null}
          {after !== null ? (
            <Button onClick={() => setAfter(null)}>最新の通知へ戻る</Button>
          ) : null}
        </>
      )}
    </div>
  );
}
